import { useCallback, useEffect, useState } from 'react'
import { WatchlistPanel, useToast } from '../ui'
import type { WatchItem } from '../ui'
import { CATEGORIES } from '../ui/CategoryChip'
import { api, ApiError, post } from '../lib/api'
import { useConfig } from '../lib/config'
import { useStream } from '../lib/useStream'
import type { Watchlist } from '../lib/types'
import { useLiveStocks } from '../lib/liveMarket'

/** Fired after any watchlist change so every panel/button on screen reloads. */
export const WATCHLIST_CHANGED = 'bceie:watchlist'
export const notifyWatchlistChanged = () => window.dispatchEvent(new Event(WATCHLIST_CHANGED))

interface Lists { data: Watchlist[]; limits: { lists: number; items: number } }

/** The user's watchlists with live data: reloads on changes, on new filings for listed stocks and every 30 s for prices. */
export function useWatchlists() {
  const [lists, setLists] = useState<Watchlist[] | null>(null)
  const [limits, setLimits] = useState<Lists['limits']>({ lists: 1, items: 5 })
  const load = useCallback(async () => {
    try { const r = await api<Lists>('/watchlists'); setLists(r.data); setLimits(r.limits) } catch { setLists(l => l ?? []) }
  }, [])
  useEffect(() => {
    void load()
    const onChange = () => { void load() }
    window.addEventListener(WATCHLIST_CHANGED, onChange)
    const t = setInterval(() => { if (document.visibilityState === 'visible') void load() }, 30000)
    return () => { window.removeEventListener(WATCHLIST_CHANGED, onChange); clearInterval(t) }
  }, [load])
  useStream(['filing.created', 'filing.updated'], e => {
    const sym = (e.data as { symbol?: string } | null)?.symbol
    if (!sym || lists?.some(l => l.items.some(i => i.symbol === sym))) void load()
  })
  return { lists, limits, reload: load }
}

/** Left-rail watchlist used on the dashboard and stock pages. `activeSymbol` highlights the stock being viewed. */
export function WatchlistSidebar({ activeSymbol }: { activeSymbol?: string }) {
  const cfg = useConfig()
  const toast = useToast()
  const { lists, limits, reload } = useWatchlists()
  const [active, setActive] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const live = useLiveStocks()

  // Keep a valid selection; on a stock page prefer the list that contains that stock.
  useEffect(() => {
    if (!lists) return
    setActive(a => {
      if (a && lists.some(l => l.id === a)) return a
      const withSym = activeSymbol ? lists.find(l => l.items.some(i => i.symbol === activeSymbol)) : undefined
      return withSym?.id ?? lists[0]?.id ?? null
    })
  }, [lists, activeSymbol])

  const createList = async () => {
    const name = window.prompt('Name for the new watchlist')?.trim()
    if (!name) return
    try { const r = await post<{ data: Watchlist }>('/watchlists', { name }); await reload(); setActive(r.data.id); notifyWatchlistChanged() }
    catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not create the list.' }) }
  }

  const addSymbol = async (symbol: string) => {
    setBusy(true)
    try {
      let listId = active
      if (!listId) { const r = await post<{ data: Watchlist }>('/watchlists', { name: 'My watchlist' }); listId = r.data.id }
      await post(`/watchlists/${listId}/items`, { symbol })
      toast.show({ tone: 'success', message: `${symbol} added.` })
      setActive(listId)
      notifyWatchlistChanged()
    } catch (e) {
      toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not add that stock.' })
    } finally { setBusy(false) }
  }

  const current = lists?.find(l => l.id === active)
  const items: WatchItem[] = (current?.items ?? []).map(i => ({
    symbol: i.symbol, exchange: 'NSE', ltp: live(i.symbol)?.ltp ?? i.ltp, changePercent: live(i.symbol)?.change_pct ?? i.change_pct,
    filing: i.filing_category ? (CATEGORIES[i.filing_category]?.label ?? i.filing_category.replace(/_/g, ' ')) : null,
  }))

  return (
    <WatchlistPanel
      lists={(lists ?? []).map(l => ({ id: String(l.id), name: l.name }))}
      activeId={active ? String(active) : ''}
      onSelect={id => setActive(Number(id))}
      items={items}
      activeSymbol={activeSymbol}
      state={lists === null ? 'loading' : 'ready'}
      showPrices={cfg.prices_enabled}
      onCreateList={(lists?.length ?? 0) < limits.lists ? () => void createList() : undefined}
      onSearchAdd={q => void addSymbol(q)}
      editHref="/watchlist"
      busy={busy}
    />
  )
}
